import { Link } from "wouter";
import { useEntitlement } from "../../api/queries";
import { useAuth } from "../../auth/AuthContext";
import { canSeeBilling } from "../../auth/roleView";
import { Banner } from "../../components/Banner";
import { entitlementBanner } from "./entitlementRules";

/**
 * App-level licensing banner — answers "why was my node refused" before an
 * operator ever hits the refusal, by surfacing degraded license states from
 * wherever they are in the app, not only on a Billing page they may never
 * open. Mounted once in `AppShell`.
 *
 * Gated on `canSeeBilling`: `GET /api/entitlement` is `billing:view`
 * server-side (admin + billing_admin only — server/internal/entitlements/
 * http.go). An operator/viewer would only ever get a 403 here, and there is
 * nothing actionable for them regardless (only `admin` holds `node:create`,
 * and `admin` always holds `billing:view` too — see roleView.ts). This is a
 * coherence choice, not a control; the server enforces the grant on every
 * request (S-8) whether or not this component ever fires.
 *
 * Silent on a fetch error (no session data yet, or the request itself
 * failed): a missing banner is the safe failure mode here — this is a
 * proactive nicety layered on top of the (always-shown) 402 handling at the
 * point of action, not the only place a limit is ever explained.
 */
export function EntitlementBanner() {
  const { session } = useAuth();
  const enabled = canSeeBilling(session?.role);
  const { data } = useEntitlement({ enabled });

  if (!enabled || !data) return null;

  const spec = entitlementBanner(data);
  if (!spec) return null;

  return (
    <Banner
      tone={spec.severity}
      title={spec.title}
      action={
        <Link href="/billing" className="banner__link">
          View plan
        </Link>
      }
    >
      {spec.description}
    </Banner>
  );
}
